import { Card, Empty } from 'antd';
import type { DashboardWidget } from '@/api/dashboardApi';

interface ChartDataItem {
  label: string;
  value: number;
}

interface ChartWidgetProps {
  widget: DashboardWidget;
  data?: ChartDataItem[];
  loading?: boolean;
}

/**
 * ChartWidget renders Bar, Pie, or Line charts.
 * Uses native SVG for zero-dependency rendering.
 * In production, replace with @ant-design/charts for richer interactivity.
 */
export default function ChartWidget({ widget, data, loading }: ChartWidgetProps) {
  const chartType = (widget.config?.chart_type as string) || widget.type || 'bar_chart';

  if (!data || data.length === 0) {
    return (
      <Card title={widget.title} size="small" loading={loading} style={{ height: '100%' }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </Card>
    );
  }

  const maxVal = Math.max(...data.map((d) => d.value), 1);

  const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16'];

  const renderBarChart = () => {
    const barHeight = 28;
    const gap = 6;
    const svgHeight = data.length * (barHeight + gap);
    return (
      <svg width="100%" height={svgHeight} viewBox={`0 0 400 ${svgHeight}`} preserveAspectRatio="xMinYMin meet">
        {data.map((item, i) => {
          const y = i * (barHeight + gap);
          const barWidth = (item.value / maxVal) * 280;
          return (
            <g key={item.label}>
              <text x={0} y={y + 18} fontSize={12} fill="#595959">{item.label}</text>
              <rect x={100} y={y + 2} width={barWidth} height={barHeight - 4} rx={3} fill={COLORS[i % COLORS.length]} />
              <text x={105 + barWidth} y={y + 18} fontSize={11} fill="#8c8c8c">{item.value.toLocaleString()}</text>
            </g>
          );
        })}
      </svg>
    );
  };

  const renderPieChart = () => {
    const total = data.reduce((s, d) => s + d.value, 0) || 1;
    const cx = 100, cy = 100, r = 80;
    let cumAngle = -Math.PI / 2;
    const slices = data.map((item, i) => {
      const angle = (item.value / total) * 2 * Math.PI;
      const startX = cx + r * Math.cos(cumAngle);
      const startY = cy + r * Math.sin(cumAngle);
      cumAngle += angle;
      const endX = cx + r * Math.cos(cumAngle);
      const endY = cy + r * Math.sin(cumAngle);
      const largeArc = angle > Math.PI ? 1 : 0;
      const d = `M${cx},${cy} L${startX},${startY} A${r},${r} 0 ${largeArc},1 ${endX},${endY} Z`;
      return <path key={item.label} d={d} fill={COLORS[i % COLORS.length]} />;
    });
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <svg width={200} height={200} viewBox="0 0 200 200">{slices}</svg>
        <div>
          {data.map((item, i) => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ width: 12, height: 12, borderRadius: 2, background: COLORS[i % COLORS.length], display: 'inline-block' }} />
              <span style={{ fontSize: 12 }}>{item.label}: {item.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLineChart = () => {
    const w = 380, h = 180, padX = 40, padY = 20;
    const plotW = w - padX * 2;
    const plotH = h - padY * 2;
    const points = data.map((item, i) => ({
      x: padX + (i / Math.max(data.length - 1, 1)) * plotW,
      y: padY + plotH - (item.value / maxVal) * plotH,
      ...item,
    }));
    const polyline = points.map((p) => `${p.x},${p.y}`).join(' ');
    return (
      <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMinYMin meet">
        <polyline points={polyline} fill="none" stroke="#1890ff" strokeWidth={2} />
        {points.map((p) => (
          <g key={p.label}>
            <circle cx={p.x} cy={p.y} r={3} fill="#1890ff" />
            <text x={p.x} y={h - 2} fontSize={10} fill="#8c8c8c" textAnchor="middle">{p.label}</text>
          </g>
        ))}
      </svg>
    );
  };

  const chartMap: Record<string, () => JSX.Element> = {
    bar_chart: renderBarChart,
    pie_chart: renderPieChart,
    line_chart: renderLineChart,
  };

  const renderer = chartMap[chartType] ?? renderBarChart;

  return (
    <Card title={widget.title} size="small" loading={loading} style={{ height: '100%' }}>
      {renderer()}
    </Card>
  );
}
